/**
 * Hungarian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal te (locked in l10n/locales.md).
 *
 * Platform terms follow Hungarian UI conventions (Téma for theme,
 * beszélgetés for conversation, párosítás for pair, elvetés for dismiss,
 * fogadó eszköz for a receiving surface); "Glance" is the untranslated
 * feature brand name (glossary D1) and "klip" is a locked loanword (D2).
 *
 * Plural notes: Hungarian is a {one, other} CLDR locale, but after a
 * numeral the noun stays SINGULAR (5 eszköz, not 5 eszközök), so the two
 * grammatical forms are usually identical text. The `selectPlural("hu", …)`
 * shape is kept for parity with the en catalog.
 *
 * Placeholder rule: Hungarian case suffixes are never attached to a
 * substituted value (vowel harmony is unknowable), so `on %s` / `via %s`
 * style constructions are restructured with a colon or a head noun.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Csatlakoztatva",
    statusReconnecting: "Újracsatlakozás…",
    statusDisconnected: "Nincs kapcsolat",
    openSettings: "Beállítások megnyitása",
    pairedFallbackDevice: "a telefonod",
    publicModeToggleEnable: "Értesítések tartalmának elrejtése",
    publicModeToggleDisable: "Értesítések tartalmának megjelenítése",
  },

  notifications: {
    replyAriaLabel: "Válasz",
    replyPlaceholder: "Válasz…",
    replySend: "Küldés",
    sent: "Elküldve",
    failedToSend: "A küldés sikertelen",
    unread: "Olvasatlan",
    dismiss: "Elvetés",
    notificationImageAlt: "Értesítés képe",
  },

  conversations: {
    replyAriaLabel: "Válasz",
    replyPlaceholder: "Válasz…",
    replySend: "Küldés",
    sent: "Elküldve",
    failedToSend: "A küldés sikertelen",
    dismiss: "Beszélgetés elvetése",
    messagesListLabel: "Üzenetek ebben a beszélgetésben",
    deviceOwner: "Te",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Nincs olvasatlan üzenet"
        : `${count} ${selectPlural("hu", count, {
            one: "olvasatlan üzenet",
            other: "olvasatlan üzenet",
          })}`,
  },

  list: {
    today: "Ma",
    yesterday: "Tegnap",
    searchNotifications: "Értesítések keresése",
    clearSearch: "Keresés törlése",
    clearAll: "Összes törlése",
    markAllRead: "Összes olvasottnak jelölése",
    groupCheckboxLabel: "Csoportosítás",
    noNotificationsTitle: "Még nincsenek értesítések",
    noNotificationsDesc: "A telefonodról tükrözött új üzenetek itt jelennek meg.",
    noMatchesTitle: "Nincs találat",
    notificationsList: "Értesítések",
    privacyAppList: "Értesítések alkalmazásonként",
    clear: "Törlés",
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("hu", count, { one: "találat", other: "találat" })} törlése`,
    noMatchesDesc: (query: string): string => `Nincs a keresésnek megfelelő értesítés: „${query}”.`,
    appNotificationsGroup: (appName: string): string => `${appName} értesítései`,
    dismissAllFrom: (appName: string): string => `Az összes elvetése innen: ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("hu", count, { one: "üzenet", other: "üzenet" })
          : kind === "calls"
            ? selectPlural("hu", count, { one: "hívás", other: "hívás" })
            : selectPlural("hu", count, { one: "értesítés", other: "értesítés" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "elvetés",
    kbdNavigate: "navigálás",
    kbdOrSeparator: "vagy",
  },

  settings: {
    back: "Vissza az értesítésekhez",
    settings: "Beállítások",
    appearance: "Megjelenés",
    theme: "Téma",
    themeAuto: "Automatikus",
    themeLight: "Világos",
    themeDark: "Sötét",
    language: "Nyelv",
    languageSystemDefault: "Rendszer alapértelmezése",
    notificationsSection: "Értesítések",
    osNotifications: "Rendszerértesítések",
    callNotifications: "Hívásértesítések",
    privacySection: "Adatvédelem",
    analyticsToggle: "Névtelen használati statisztika",
    analyticsToggleDescription: "Csak számlálók és mérföldkövek — soha az értesítéseid.",
    connection: "Kapcsolat",
    status: "Állapot",
    statusConnected: "Csatlakoztatva",
    statusReconnecting: "Újracsatlakozás…",
    statusDisconnected: "Nincs kapcsolat",
    disconnectFromPhone: "Kapcsolat bontása a telefonnal",
    disconnectConfirm: "Biztos vagy benne? Ez megszünteti az eszközöd párosítását.",
    yesDisconnect: "Igen, bontás",
    cancel: "Mégse",
    aboutAndSupport: "Névjegy és támogatás",
    version: "Verzió",
    encryption: "Titkosítás",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Segítségre van szükséged? Írj a __EMAIL__ címre",
    disconnectFailed: "A kapcsolat bontása sikertelen — próbáld újra",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `A kapcsolat bontva, de a helyi törlés sikertelen: ${errorMsg}. Töltsd újra az oldalt.`,
    disconnectLocalCleanupFailed:
      "A kapcsolat bontva, de a helyi törlés sikertelen. Töltsd újra az oldalt.",
  },

  pairing: {
    enterCodeInstruction: "Írd be ezt a kódot a Reflecto alkalmazásba",
    pairedSuccessfully: "Sikeres párosítás!",
    pairingFailed: "A párosítás sikertelen",
    requestingPairingCode: "Párosítási kód kérése…",
    codeExpired: "A kód lejárt",
    tryAgain: "Próbáld újra",
    failedToStartPairing: "A párosítás indítása sikertelen",
    needAppText: "Szükséged van az Android-alkalmazásra? Írj a __EMAIL__ címre a hozzáférésért.",
  },

  dnd: {
    title: "Ne zavarjanak",
    description: "Szüneteltesd az értesítéseket ezen az eszközön a következő egy órára.",
    snooze1h: "Szüneteltetés 1 órára",
    snoozeActiveUntil: (formattedTime: string): string => `Aktív eddig: ${formattedTime}`,
    cancelSnooze: "Mégse",
  },

  connectors: {
    title: "Csatlakoztatott szolgáltatások",
    emptyBody:
      "Még nincsenek csatlakoztatott szolgáltatások. Csatlakoztasd a ChatGPT-t vagy a Claude-ot a csatlakozóbeállításaikból, majd hagyd jóvá a kódot a telefonodon.",
    scopeSend: "Küldhet értesítéseket",
    added: (date: string): string => `Hozzáadva ${date}`,
    lastUsed: (relative: string): string => `Utoljára használva ${relative}`,
    neverUsed: "Soha nem használt",
    revoke: "Kapcsolat bontása",
    revokeConfirm: (label: string): string =>
      `Bontod a(z) ${label} kapcsolatát? Azonnal elveszíti a hozzáférést.`,
    revokeAction: "Kapcsolat bontása",
    revokeConfirmLabel: (label: string): string => `A(z) ${label} kapcsolatának bontása`,
    cancel: "Mégse",
    loadError: "A csatlakoztatott szolgáltatásokat nem sikerült betölteni. Próbáld újra.",
    revokeError: "A szolgáltatás kapcsolatát nem sikerült bontani. Próbáld újra.",
    retry: "Újra",
  },

  feedback: {
    title: "Visszajelzés küldése",
    close: "Bezárás",
    messageLabel: "Az üzeneted",
    messagePlaceholder: "Hibát találtál, van egy ötleted, vagy valami nem stimmel? Írd meg nekünk.",
    categoryLabel: "Kategória",
    categoryBug: "Hiba",
    categoryIdea: "Ötlet",
    categoryOther: "Egyéb",
    emailLabel: "E-mail (nem kötelező, csak ha választ szeretnél)",
    emailPlaceholder: "te@pelda.hu",
    emailHelp: "Nem teszünk fel semmilyen listára.",
    disclosure:
      "Az üzeneted a Reflecto szerverére kerül, és rögzítjük a belső hibakövetőnkben. Csatoljuk az alkalmazásod verzióját és egy, a párosításkor kiadott eszközazonosítót, hogy összefüggésbe hozhassuk a jelentéseket. Nem látjuk az értesítéseidet, a névjegyeidet, sem más személyes adatot. Az e-mail megadása nem kötelező — csak akkor add meg, ha választ szeretnél. Nem teszünk fel semmilyen listára.",
    submit: "Visszajelzés küldése",
    sending: "Küldés…",
    success: "Köszönjük — ember olvassa, általában 24 órán belül",
    error: "A küldés sikertelen. Próbáld újra.",
    done: "Kész",
    sendFeedback: "Visszajelzés küldése",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Bezárás",
    aiSummaryTag: "AI-összefoglaló",
    errorAi: "Most nem sikerült összefoglalót készíteni.",
    retryCta: "Próbáld újra",
    downloadHeading: "Kérj AI-összefoglalókat",
    downloadBody:
      "Nézd meg egy eszközön futó AI-összegzésben, miről maradtál le. Teljes egészében az eszközödön fut.",
    downloadCta: "Modell letöltése",
    downloadPrivacyNote: "Egyszeri letöltés. A szerver soha nem látja az értesítéseidet.",
    downloadingHeading: "Modell letöltése…",
    caughtUp: "Mindennel naprakész vagy — most semmi sem igényli a figyelmedet.",
    needsYou: "Figyelmet igényel",
    quiet: "Egyéb",
    scanning: (pending: number): string => `${pending} elem összegzése…`,
    jump: "Értesítés megnyitása",
    affordanceReply: "Válasz",
    affordanceOpen: "Megnyitás",
    affordanceCopyOtp: "Kód másolása",
    buttonLabel: "Glance",
  },

  update: {
    title: "Frissítés érhető el",
    description: "A Reflecto új verziója készen áll. A frissítéshez töltsd újra.",
    reload: "Újratöltés",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Ezt az eszközt így neveztük el: „${name}”.`,
    cardLooksGood: "Jó lesz",
    cardEdit: "Szerkesztés",
    cardAriaLabel: "Eszköz nevének megerősítése",
    renameTitle: "Eszköz átnevezése",
    renameLabel: "Eszköz neve",
    renameSave: "Mentés",
    renameCancel: "Mégse",
    renameFailed: "Az átnevezés sikertelen. Próbáld újra.",
  },

  collision: {
    title: (name: string): string => `Már van egy „${name}” nevű eszközöd`,
    subtitle: "Minek nevezzük ezt?",
    chipWork: (name: string): string => `Munka ${name}`,
    chipHome: (name: string): string => `Otthon ${name}`,
    chipPersonal: (name: string): string => `Saját ${name}`,
    customLabel: "Egyéni név",
    save: "Mentés",
    useAnyway: "Mégis az eredeti nevet használom",
    saveFailed: "Az új nevet nem sikerült elmenteni. Próbáld újra.",
    ariaLabel: "Ismétlődő eszköznév feloldása",
  },

  clipboard: {
    title: "Vágólap",
    empty: "A többi eszközödről érkező klipek itt jelennek meg. Koppints a másoláshoz.",
    from: (label: string): string => `innen: ${label}`,
    copy: "Másolás a vágólapra",
    copied: "Másolva",
    reveal: "Felfedés",
    hide: "Elrejtés",
    maskedHint: "A tartalom rejtve — a megjelenítéshez nyomd meg a Felfedés gombot",
    sensitiveChip: "Érzékeny",
    cloudChip: "az API-tokenedből",
    sendPlaceholder: "Illeszd be a küldendő szöveget…",
    sendCta: "Küldés",
    sendSensitiveToggle: "Megjelölés érzékenyként",
    sendSuccess: (n: number): string =>
      `Elküldve ${n} ${selectPlural("hu", n, { one: "eszközre", other: "eszközre" })}.`,
    sendPartial: (n: number, total: number): string =>
      `${n}/${total} ${selectPlural("hu", total, { one: "eszköz", other: "eszköz" })} elérve.`,
    sendNoDevices: "Nincs párosított eszköz, amelyre küldeni lehetne.",
    sendFailed: "A küldés sikertelen. Próbáld újra.",
    sendEmpty: "Nincs mit küldeni.",
    dockExpand: "Vágólap-előzmények megjelenítése",
    dockCollapse: "Összecsukás",
    dockSendTitle: "Küldés az eszközeidre",
    dockFromClipboard: (chars: number): string =>
      `Vágólapról · ${chars} ${selectPlural("hu", chars, {
        one: "karakter",
        other: "karakter",
      })}`,
    dockSend: (n: number): string => `Küldés ${n} eszközre`,
    dockCancel: "Mégse",
    dockRecentHeading: "Legutóbbi",
    dockEmptyClipboard: "A vágólapod üres.",
    shareTitle: "Küldés az eszközeidre",
    shareReview:
      "Ellenőrizd, mit küldesz. A párosított eszközeidre kerül, végpontok között titkosítva.",
    shareSend: "Küldés",
    shareCancel: "Mégse",
    shareNotPaired: "A megosztott szöveg küldéséhez előbb párosíts egy eszközt.",
    shareOpenApp: "Reflecto megnyitása",
  },
  install: {
    title: "Reflecto telepítése",
    body: "Add a kezdőképernyődhöz a gyorsabb hozzáférésért.",
    cta: "Telepítés",
    dismissAria: "Telepítési kérés elvetése",
  },
  common: {
    loading: "Betöltés",
  },
  errors: {
    notPaired: "Nincs párosítva",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} (${os})`,
    webFallback: "Webböngésző",
  },

  palette: {
    placeholder: "Írj egy parancsot…",
    noResults: "Nincs egyező parancs",
    ariaLabel: "Parancspaletta",
    clearAll: "Összes értesítés törlése",
    glance: "Glance-összefoglaló",
    cancelSnooze: "Szüneteltetés megszakítása",
    copyClip: "Legutóbb kapott klip másolása",
  },
} satisfies MessagesShape;
